import { useEffect, useRef, useState } from 'react';

import { AutoCompleteComboBox, InputProps, Icon, useColorModeValue, Button, Stack } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { BiSearch } from 'react-icons/bi';
import { localStorageKeys } from 'storageKeys';

import { FilterMap, Suggestion } from 'hooks/data/search/types';
import useSuggestions from 'hooks/data/search/useSuggestions';
import SearchSuggestion from 'pages/Search/components/SearchSuggestion';
import { incrementSearch } from 'utilities/consent/cookies';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

type Props = Omit<InputProps, 'onChange'> & {
  onSearch: (value: string, item_type?: string) => void;
  defaultValue: string;
  filters: FilterMap;
  btnVariant?: string;
  includeButton?: boolean;
};

type RecentSearch = {
  text: string;
  item_type?: string;
};

const loadRecentSearches = () => {
  const recentSearches = localStorage.getItem(localStorageKeys.RECENT_SEARCHES);
  if (!recentSearches) return [];
  const parsed = JSON.parse(recentSearches) as Array<RecentSearch | string>;
  return parsed
    .map((item) => (typeof item === 'string' ? { text: item } : item))
    .filter((item): item is RecentSearch => Boolean(item?.text));
};

const saveRecentSearches = (recentSearches: RecentSearch[]) => {
  localStorage.setItem(localStorageKeys.RECENT_SEARCHES, JSON.stringify(recentSearches));
};

const AutoCompleteSearch = ({
  onSearch,
  defaultValue: defaultValue,
  filters,
  btnVariant = 'subtle',
  includeButton = true,
  ...rest
}: Props) => {
  const [inputValue, setInputValue] = useState(defaultValue || '');
  const [debouncedQuery, setDebouncedQuery] = useState(inputValue);

  const { data: suggestions, isLoading } = useSuggestions({ query: debouncedQuery, filters });

  const recentSearches = loadRecentSearches();
  const bg = useColorModeValue('white', 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.800', 'white');
  const grayText = useColorModeValue('gray.500', 'gray.400');

  const myRef = useRef<HTMLInputElement>();

  const shouldShowRecentSearches = debouncedQuery.length === 0;

  const handleSubmit = (v: string, item_type?: string) => {
    myRef.current?.blur();
    if (v && !recentSearches.some((item) => item.text === v)) {
      const updatedRecentSearches = [...recentSearches];
      if (recentSearches.length >= 5) {
        updatedRecentSearches.pop();
      }
      saveRecentSearches([{ text: v, item_type }, ...updatedRecentSearches]);
    }
    onSearch(v, !item_type ? 'activity' : item_type);
  };

  const trackSelection = (selectedItem: Suggestion | undefined) => {
    if (!selectedItem) return;

    const eventName = shouldShowRecentSearches
      ? MIXPANEL_EVENTS.RECENT_SEARCH_CLICKED
      : MIXPANEL_EVENTS.SEARCH_SUGGESTION_CLICKED;
    const query = shouldShowRecentSearches ? selectedItem.text || inputValue : debouncedQuery;

    const options = (shouldShowRecentSearches ? recentSearches : (suggestions?.slice(0, 6) ?? [])).map((s) => s.text);
    const selectedText = selectedItem.text || inputValue;
    const index = options.indexOf(selectedText);

    mixpanel.track(eventName, {
      query,
      filters,
      item_type: selectedItem.item_type,
      name: selectedItem.text,
      sector: selectedItem.sector,
      position: index !== -1 ? index + 1 : undefined,
    });
    incrementSearch();
  };

  // Update debouncedQuery after debounce delay (100ms when empty, 300ms otherwise)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(inputValue), inputValue.length === 0 ? 100 : 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  return (
    <Stack w="100%" direction={{ base: 'column', md: 'row' }} spacing={5}>
      <Stack w="100%">
        <AutoCompleteComboBox
          autoFocus
          leftElement={<Icon color={grayText} as={BiSearch} />}
          inputValue={inputValue}
          onChange={({ inputValue: newValue, selectedItem }) => {
            handleSubmit(newValue ?? '', selectedItem?.item_type);
            trackSelection(selectedItem);
          }}
          onChangeInputValue={(v) => setInputValue(v)}
          options={
            debouncedQuery.length > 0
              ? suggestions?.slice(0, 6) || []
              : recentSearches.map((search) => ({
                  text: search.text,
                  highlight: search.text,
                  item_type: search.item_type ?? '',
                  sector: [],
                }))
          }
          itemToString={(item) => item?.text ?? ''}
          placeholder="Search for an activity or a product..."
          isLoading={isLoading}
          renderOption={(item) => <SearchSuggestion item={item} isRecentSearch={debouncedQuery.length === 0} />}
          colorScheme="green"
          size={'lg'}
          bg={bg}
          color={textColor}
          {...rest}
        />
      </Stack>
      {includeButton && (
        <Button
          size="lg"
          rightIcon={<Icon as={BiSearch} />}
          onClick={() => onSearch(inputValue, 'activity')}
          variant={btnVariant}
        >
          Search
        </Button>
      )}
    </Stack>
  );
};

export default AutoCompleteSearch;
